import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { fileToBase64 } from "@/lib/api";
import { GALLERY_CATEGORIES } from "@/components/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function GalleryManager() {
  const [items, setItems] = useState([]);
  const [caption, setCaption] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("plats");
  const [scope, setScope] = useState("restaurant");
  const [variant, setVariant] = useState("");
  const [pairKey, setPairKey] = useState("");
  const [photo, setPhoto] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/gallery").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(await fileToBase64(file));
  };

  const add = async (e) => {
    e.preventDefault();
    if (!photo) {
      toast.error("Choisissez une photo.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/gallery", {
        photo,
        caption,
        title,
        description,
        category,
        order: items.length,
        scope,
        variant,
        pair_key: variant ? pairKey : "",
      });
      toast.success("Photo ajoutée.");
      setPhoto("");
      setCaption("");
      setTitle("");
      setDescription("");
      load();
    } catch {
      toast.error("Erreur lors de l'ajout.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/gallery/${id}`);
    toast.success("Photo supprimée.");
    load();
  };

  return (
    <div data-testid="gallery-manager">
      <h2 className="mb-8 text-lg font-semibold text-ivory">Galerie — {items.length} photo(s)</h2>

      <form onSubmit={add} className="mb-10 flex flex-wrap items-end gap-4 rounded-lg border border-white/10 bg-coal/60 p-6" data-testid="gallery-add-form">
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Photo</label>
          {photo && <img src={photo} alt="" className="mb-2 h-16 w-16 rounded-md border border-white/10 object-cover" />}
          <input type="file" accept="image/*" onChange={uploadPhoto} className="text-xs text-ivory/60 file:mr-3 file:rounded-md file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-xs file:text-ivory" data-testid="gallery-photo-upload" />
        </div>
        <div className="min-w-52 flex-1">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Titre (affiché sous la photo)</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="ex : Bureaux après intervention" className="input-dark !h-11" data-testid="gallery-title-input" />
        </div>
        <div className="min-w-52 flex-1">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Description (facultative)</label>
          <input value={description} onChange={(e) => setDescription(e.target.value)} className="input-dark !h-11" data-testid="gallery-description-input" />
        </div>
        <div className="min-w-40">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Légende (facultative)</label>
          <input value={caption} onChange={(e) => setCaption(e.target.value)} className="input-dark !h-11" data-testid="gallery-caption-input" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Univers</label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="!h-11 w-44 border-white/10 bg-white/5 text-sm text-ivory" data-testid="gallery-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-coal text-ivory">
              {GALLERY_CATEGORIES.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Activité</label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="!h-11 w-48 border-white/10 bg-white/5 text-sm text-ivory" data-testid="gallery-scope-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-coal text-ivory">
              <SelectItem value="restaurant">Palais Maasaï</SelectItem>
              <SelectItem value="services">Maasaï Services</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Type</label>
          <Select value={variant || "normal"} onValueChange={(v) => setVariant(v === "normal" ? "" : v)}>
            <SelectTrigger className="!h-11 w-40 border-white/10 bg-white/5 text-sm text-ivory" data-testid="gallery-variant-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-coal text-ivory">
              <SelectItem value="normal">Galerie</SelectItem>
              <SelectItem value="avant">Avant</SelectItem>
              <SelectItem value="apres">Après</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {variant && (
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
              Clé de paire (identique pour l'avant et l'après)
            </label>
            <input value={pairKey} onChange={(e) => setPairKey(e.target.value)} placeholder="ex : salon-1" className="input-dark !h-11 w-44" data-testid="gallery-pairkey-input" />
          </div>
        )}
        <button type="submit" disabled={saving} className="btn-primary !h-11 !px-6 !text-[11px]" data-testid="gallery-add-button">
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          Ajouter
        </button>
      </form>

      {items.length === 0 ? (
        <p className="text-sm text-ivory/40" data-testid="gallery-empty">Aucune photo pour le moment.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="gallery-admin-grid">
          {items.map((item) => (
            <div key={item.id} className="group relative overflow-hidden rounded-lg border border-white/10" data-testid={`gallery-row-${item.id}`}>
              <img src={item.photo} alt={item.caption} className="aspect-square w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-black/70 px-3 py-2 backdrop-blur-sm">
                <span className="truncate text-xs text-ivory/70">
                  <span className="mr-2 uppercase tracking-wider text-gold/70">
                    {item.scope === "services" ? "Services" : GALLERY_CATEGORIES.find((c) => c.id === item.category)?.label || "Intérieur"}
                  </span>
                  {item.variant && (
                    <span className="mr-2 rounded border border-copper/50 px-1 py-0.5 text-[10px] uppercase text-copper">
                      {item.variant === "avant" ? "Avant" : "Après"} {item.pair_key}
                    </span>
                  )}
                  {item.title || item.caption || "—"}
                </span>
                <button onClick={() => remove(item.id)} aria-label="Supprimer" className="text-ivory/50 transition-colors hover:text-red-400" data-testid={`gallery-delete-${item.id}`}>
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
